// Project workspace: loads the project, drives roadmap → document stages
// with live streaming "thinking".

import React from "react";
import { useParams } from "react-router-dom";
import type { ProjectDetail, Roadmap } from "@asmg/shared";
import { api, streamGenerate } from "../api/client";
import { Button, ErrorBanner, Spinner } from "../components/ui";
import { RoadmapEditor } from "../components/RoadmapEditor";
import { DocumentEditor } from "../components/DocumentEditor";
import { SettingsPanel } from "../components/SettingsPanel";
import { ThinkingPanel, type ThinkingState } from "../components/ThinkingPanel";

type Tab = "roadmap" | "document";

const IDLE: ThinkingState = { active: false, status: "", text: "" };

export function ProjectView() {
  const { id = "" } = useParams();
  const [detail, setDetail] = React.useState<ProjectDetail | null>(null);
  const [error, setError] = React.useState<unknown>(null);
  const [tab, setTab] = React.useState<Tab>("roadmap");
  const [busy, setBusy] = React.useState<null | "roadmap" | "document">(null);
  const [thinking, setThinking] = React.useState<ThinkingState>(IDLE);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const d = await api.getProject(id);
      setDetail(d);
      if (d.pages.length > 0) setTab("document");
    } catch (e) {
      setError(e);
    }
  }, [id]);

  React.useEffect(() => {
    void load();
  }, [load]);

  async function genRoadmap() {
    setBusy("roadmap");
    setError(null);
    setThinking({ active: true, status: "Starting…", text: "" });
    await streamGenerate(`/api/projects/${id}/roadmap/stream`, {
      onStatus: (m) => setThinking((t) => ({ ...t, status: m })),
      onDelta: (txt) => setThinking((t) => ({ ...t, text: t.text + txt })),
      onDone: (data) => {
        setDetail((d) => (d ? { ...d, roadmap: data as Roadmap } : d));
        setThinking((t) => ({ ...t, active: false, status: "Roadmap ready." }));
        setBusy(null);
      },
      onError: (msg) => {
        setError(new Error(msg));
        setThinking((t) => ({ ...t, active: false, status: "Failed." }));
        setBusy(null);
      },
    });
  }

  async function genDocument() {
    setBusy("document");
    setError(null);
    setThinking({ active: true, status: "Starting…", text: "" });
    await streamGenerate(`/api/projects/${id}/document/stream`, {
      onStatus: (m) => setThinking((t) => ({ ...t, status: m })),
      onUnit: (u) => setThinking((t) => ({ ...t, unit: u, text: t.text + `\n\n── Unit ${u.index}/${u.total}: ${u.title} ──\n` })),
      onDelta: (txt) => setThinking((t) => ({ ...t, text: t.text + txt })),
      onDone: (data) => {
        setDetail(data as ProjectDetail);
        setTab("document");
        setThinking((t) => ({ ...t, active: false, status: "Document ready." }));
        setBusy(null);
      },
      onError: (msg) => {
        setError(new Error(msg));
        setThinking((t) => ({ ...t, active: false, status: "Failed." }));
        setBusy(null);
      },
    });
  }

  function onRoadmapChange(roadmap: Roadmap) {
    setDetail((d) => (d ? { ...d, roadmap } : d));
  }

  if (!detail && !error) return <div className="container"><Spinner label="Loading project…" /></div>;
  if (!detail) return <div className="container"><ErrorBanner error={error} onRetry={load} /></div>;

  const hasRoadmap = detail.roadmap.units.length > 0;
  const hasDocument = detail.pages.length > 0;

  return (
    <div className="container">
      <div className="row-between">
        <div>
          <h2 style={{ marginBottom: 4 }}>{detail.topic}</h2>
          <span className="muted">{detail.roadmapConfirmed ? "confirmed" : "draft"}</span>
        </div>
        <SettingsPanel
          projectId={id}
          documentType={detail.documentType}
          language={detail.language}
          llmModel={detail.llmModel}
          onUpdated={(patch) => setDetail((d) => (d ? { ...d, ...patch } : d))}
        />
      </div>

      <div className="page-list" style={{ marginTop: 12 }}>
        <button className={`page-tab ${tab === "roadmap" ? "active" : ""}`} onClick={() => setTab("roadmap")}>
          Roadmap
        </button>
        <button
          className={`page-tab ${tab === "document" ? "active" : ""}`}
          onClick={() => setTab("document")}
          disabled={!hasDocument}
        >
          Document
        </button>
      </div>

      <ErrorBanner error={error} onRetry={load} />

      <ThinkingPanel state={thinking} />

      {tab === "roadmap" && (
        <div>
          {!hasRoadmap ? (
            <div className="card row-between">
              <span className="muted">No roadmap yet. Generate one from your topic.</span>
              <Button onClick={genRoadmap} loading={busy === "roadmap"}>
                Generate Roadmap
              </Button>
            </div>
          ) : (
            <>
              <div className="card row-between">
                <span className="muted">Refine, then confirm to generate the document.</span>
                <div className="row">
                  <Button variant="ghost" onClick={genRoadmap} loading={busy === "roadmap"}>
                    Regenerate
                  </Button>
                  <Button onClick={genDocument} loading={busy === "document"} disabled={!detail.roadmapConfirmed}>
                    {hasDocument ? "Regenerate Document" : "Generate Document"}
                  </Button>
                </div>
              </div>
              <RoadmapEditor
                projectId={id}
                roadmap={detail.roadmap}
                confirmed={detail.roadmapConfirmed}
                onRoadmapChange={onRoadmapChange}
                onConfirmed={() => setDetail((d) => (d ? { ...d, roadmapConfirmed: true } : d))}
              />
            </>
          )}
        </div>
      )}

      {tab === "document" && hasDocument && <DocumentEditor detail={detail} onReload={load} />}
    </div>
  );
}
